// Website Schema
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "fixINN",
  "description": "Professional home repair and maintenance services. Expert technicians for all your home improvement needs.",
  "url": process.env.REACT_APP_SITE_URL || "https://fixinn.co.uk",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${process.env.REACT_APP_SITE_URL || "https://fixinn.co.uk"}/search?q={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
};

// Organization Schema
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "fixINN",
  "description": "Professional device repair and tech support services company providing expert solutions for phones, laptops, computers, watches, plus tennis coaching and computer training.",
  "url": process.env.REACT_APP_SITE_URL || "https://fixinn.co.uk",
  "logo": `${process.env.REACT_APP_SITE_URL || "https://fixinn.co.uk"}/logo512.png`,
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+1-555-FIXINN",
    "contactType": "customer service",
    "availableLanguage": "English",
    "areaServed": "US"
  },
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "US"
  },
  "sameAs": [
    "https://facebook.com/fixinn",
    "https://twitter.com/fixinn",
    "https://instagram.com/fixinn"
  ],
  "makesOffer": [
    {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Phone Repair Services",
        "description": "Screen replacement, battery replacement, charging port repairs"
      }
    },
    {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Laptop Repair Services",
        "description": "Hardware and software solutions for laptops"
      }
    },
    {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Home Tech Support",
        "description": "Wi-Fi setup, smart device configuration, troubleshooting"
      }
    },
    {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Computer Coaching",
        "description": "Personalized computer training and digital skills education"
      }
    },
    {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Tennis Stringing & Coaching",
        "description": "Professional tennis racquet stringing and coaching services"
      }
    },
    {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Watch Repair & Servicing",
        "description": "Expert watch repair and maintenance services"
      }
    }
  ]
};

// Service Schema Generator
export const createServiceSchema = (serviceName, description, price = null) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": serviceName,
  "description": description,
  "provider": {
    "@type": "Organization",
    "name": "fixINN",
    "url": process.env.REACT_APP_SITE_URL || "https://fixinn.co.uk"
  },
  "serviceType": "Repair and Support Services",
  "areaServed": {
    "@type": "Place",
    "name": "Local Service Area"
  },
  ...(price && {
    "offers": {
      "@type": "Offer",
      "price": price,
      "priceCurrency": "USD"
    }
  })
});

// Breadcrumb Schema Generator
export const createBreadcrumbSchema = (breadcrumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": breadcrumbs.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

// FAQ Schema Generator
export const createFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

// Local Business Schema
export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "fixINN",
  "description": "Professional device repair and tech support services including phone repair, laptop repair, computer support, watch repair, tennis coaching, and computer training.",
  "url": process.env.REACT_APP_SITE_URL || "https://fixinn.co.uk",
  "telephone": "+1-555-FIXINN",
  "email": "info@fixinn.com",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "US"
  },
  "openingHours": [
    "Mo-Fr 08:00-18:00",
    "Sa 09:00-17:00"
  ],
  "priceRange": "$",
  "image": `${process.env.REACT_APP_SITE_URL || "https://fixinn.co.uk"}/logo512.png`,
  "serviceArea": {
    "@type": "Place",
    "name": "Local Service Area"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "fixINN Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Phone Repair Services",
          "description": "Screen replacement, battery replacement, charging port repairs, water damage repair"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Laptop Repair Services",
          "description": "Screen replacement, battery replacement, keyboard repairs, hard drive upgrades"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Home Tech Support",
          "description": "Wi-Fi setup, smart device configuration, computer troubleshooting"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Computer Coaching",
          "description": "Basic computer skills, office applications training, cybersecurity awareness"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Tennis Stringing & Coaching",
          "description": "Racquet stringing, private coaching, group clinics"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Watch Repair & Servicing",
          "description": "Battery replacement, movement overhaul, crystal replacement"
        }
      }
    ]
  }
};

// Specific service schemas for each service
export const phoneRepairServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Phone Repair Services",
  "description": "Professional smartphone repair services including screen replacement, battery replacement, charging port repairs, water damage repair, and software fixes.",
  "provider": {
    "@type": "Organization",
    "name": "fixINN",
    "url": process.env.REACT_APP_SITE_URL || "https://fixinn.co.uk"
  },
  "serviceType": "Electronic Device Repair",
  "category": "Phone Repair",
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Phone Repair Services",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Screen Replacement" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Battery Replacement" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Charging Port Repairs" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Water Damage Repair" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Software Fixes" } }
    ]
  }
};

export const laptopRepairServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Laptop Repair Services",
  "description": "Professional laptop repair services including screen replacement, battery replacement, keyboard repairs, hard drive upgrades, and software troubleshooting.",
  "provider": {
    "@type": "Organization",
    "name": "Fixinn",
    "url": process.env.REACT_APP_SITE_URL || "https://fixinn.co.uk"
  },
  "serviceType": "Computer Repair",
  "category": "Laptop Repair"
};

export const techSupportServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Home Tech Support",
  "description": "Comprehensive home tech support including Wi-Fi setup, smart device configuration, computer troubleshooting, and data backup services.",
  "provider": {
    "@type": "Organization",
    "name": "Fixinn",
    "url": process.env.REACT_APP_SITE_URL || "https://fixinn.co.uk"
  },
  "serviceType": "Technical Support",
  "category": "Home Tech Support"
};

export const computerCoachingServiceSchema = {
  "@context": "https://schema.org",
  "@type": "EducationalService",
  "name": "Computer Coaching Services",
  "description": "Personalized computer coaching sessions covering basic skills, office applications, email setup, and cybersecurity awareness.",
  "provider": {
    "@type": "Organization",
    "name": "Fixinn",
    "url": process.env.REACT_APP_SITE_URL || "https://fixinn.co.uk"
  },
  "serviceType": "Education and Training",
  "category": "Computer Training"
};

export const tennisCoachingServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Tennis Stringing & Coaching",
  "description": "Professional tennis services including racquet stringing, grip replacement, private coaching, and group clinics.",
  "provider": {
    "@type": "Organization",
    "name": "Fixinn",
    "url": process.env.REACT_APP_SITE_URL || "https://fixinn.co.uk"
  },
  "serviceType": "Sports Coaching and Equipment Services",
  "category": "Tennis Services"
};

export const watchRepairServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Watch Repair & Servicing",
  "description": "Expert watch repair services including battery replacement, strap adjustments, movement overhaul, and crystal replacement.",
  "provider": {
    "@type": "Organization",
    "name": "Fixinn",
    "url": process.env.REACT_APP_SITE_URL || "https://fixinn.co.uk"
  },
  "serviceType": "Watch Repair",
  "category": "Timepiece Repair and Maintenance"
};